import {
    Box,
    IconButton,
    List,
    ListItem,
    TextField,
    Button,
    Typography,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";

interface Pattern {
    value: string;
    source?: {
        appId: string;
        appName: string;
        filterType: string;
        comment?: string;
    };
}

interface PatternListProps {
    patterns: Pattern[];
    onDelete?: (pattern: string) => void;
    onAdd?: (pattern: string) => void;
    placeholder?: string;
    label?: string;
    allowAdd?: boolean;
}

export function PatternList(props: PatternListProps) {
    const [newPattern, setNewPattern] = useState("");

    const handleAdd = () => {
        if (newPattern.trim() && props.onAdd) {
            props.onAdd(newPattern.trim());
            setNewPattern("");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <Box>
            {props.allowAdd && (
                <>
                    <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={props.label || "Pattern"}
                            placeholder={props.placeholder || "Enter a pattern"}
                            value={newPattern}
                            onChange={(e) => setNewPattern(e.target.value)}
                            onKeyPress={handleKeyPress}
                            sx={{ maxWidth: "600px" }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            disabled={!newPattern.trim()}
                            sx={{ minWidth: "100px", height: "40px" }}
                        >
                            Add
                        </Button>
                    </Box>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            Patterns support regular expressions. Examples:
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ marginLeft: "10px" }}>
                            • <code>^https://example\.com/.*</code> - Matches all URLs from example.com
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ marginLeft: "10px" }}>
                            • <code>.*\.example\.com</code> - Matches all subdomains of example.com
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ marginLeft: "10px" }}>
                            • <code>.*/api/.*</code> - Matches all URLs containing /api/
                        </Typography>
                    </Box>

                </>
            )}

            {props.patterns.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No patterns defined
                </Typography>
            ) : (
                <List
                    dense
                    sx={{
                        padding: "0px",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        maxHeight: "500px",
                        overflowY: "auto"
                    }}
                >
                    {props.patterns.map((pattern, index) => (
                        <ListItem
                            key={`${pattern.value}-${index}`}
                            sx={{
                                py: 0,
                                px: 1.5,
                                minHeight: "40px",
                                borderBottom: index < props.patterns.length - 1 ? "1px solid" : "none",
                                borderColor: "divider",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                            }}
                        >
                            <Box sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                                my: 0.25
                            }}>
                                <code style={{
                                    fontSize: "0.8125rem",
                                    wordBreak: "break-all",
                                    lineHeight: "1.5"
                                }}>
                                    {pattern.value}
                                </code>
                                {pattern.source?.filterType && (
                                    <Chip
                                        label={pattern.source.filterType}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            fontSize: "0.6875rem",
                                            height: "20px",
                                            "& .MuiChip-label": {
                                                px: 0.75,
                                                py: 0
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                            {pattern.source ? (
                                <Chip
                                    label={pattern.source.appName}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    sx={{
                                        fontSize: "0.6875rem",
                                        height: "20px",
                                        flexShrink: 0,
                                        "& .MuiChip-label": {
                                            px: 0.75,
                                            py: 0
                                        }
                                    }}
                                />
                            ) : props.onDelete ? (
                                <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() => props.onDelete!(pattern.value)}
                                    size="small"
                                    sx={{ p: 0.5, flexShrink: 0 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}
